/**
 * What-If input-delta chart (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 PROPOSAL row, §7 step 7/8): the chart
 * rendering for an `input_delta.inputs[]` entry whose `kind ===
 * 'time_series'`. One solid "current" series + one dashed "proposed"
 * series per changed row, color-matched per pair, x-axis rendered as
 * CATEGORIES from `x` verbatim (never date-parsed — build_input_delta
 * copies ValueHeaders straight through, which can be calendar years or
 * "Month N" period labels; parsing either as a JS Date would silently
 * corrupt the axis).
 *
 * Chart-approach note (task 13 self-review): the existing
 * AgenticV2/cards/results/charts/LineChart.tsx wrapper is built for the
 * ScenarioManager/AgenticV2 forecast-output payload shape
 * (`IChartData`/`IChartDataSeries`, scenario/preview-vs-published
 * concepts baked into its tooltip and series-construction logic). The
 * what-if input-delta payload is a different, much simpler shape
 * (before/after pairs, no scenario/dataType axis) — forcing it through
 * that wrapper's props would mean fabricating a fake IChartData just to
 * satisfy the wrapper's types, which is worse than a small direct
 * config. Per the task brief's explicit escape hatch, this component
 * builds its own compact Highcharts options directly, reusing only the
 * `colors` palette export (so pair coloring stays consistent with the
 * rest of the app) and the same `highcharts-react-official` +
 * `highcharts` import pattern already used by that file (confirmed the
 * house import convention — no new dependency).
 *
 * Responsive height (F6, design doc §4 amended 2026-08-20): ~380px when
 * this is the proposal's only input, ~260px when the proposal renders
 * more than one input — the PROPOSAL canvas must fit the viewport whole
 * in the single-input case, and chart height is the compression lever
 * for the multi-input case (the canvas itself may scroll as a fallback;
 * the bar never does, D10). `compact` is a plain boolean prop rather
 * than the parent passing a raw pixel height — this component owns its
 * own two sanctioned sizes, not an arbitrary caller-supplied number.
 */
import React, { useMemo } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Box, Typography } from '@mui/material';

import { colors } from 'src/components/AgenticV2/cards/results/charts/LineChart';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';

import { InputDeltaInput } from '../types';
import { YearWindow, yearKeepMask, applyKeepMask } from '../execYearWindow';

interface InputDeltaChartProps {
  input: InputDeltaInput;
  // True when the proposal renders more than one input (F6) — the
  // chart compresses to ~260px instead of its normal ~380px so a
  // multi-input proposal has a chance of fitting the viewport without
  // the canvas needing to scroll.
  compact?: boolean;
  // Exec AI year window (C1): clips the x-axis (and each series in step) to the
  // instance's window. Absent/null bounds = show all years.
  yearWindow?: YearWindow | null;
}

// Magnitude divisors keyed by the FormatKey table's magnitudeTruncation
// bucket (0 = ones, 1 = thousands, 2 = millions). Matches the platform
// formatter (getFormattedValue): the value is DIVIDED by the factor with no
// letter suffix appended — the platform never emits "K"/"M", so neither do we.
const TRUNCATION_DIVISOR: Record<number, number> = {
  0: 1,
  1: 1000,
  2: 1000000,
};

// Format one numeric value against a platform FormatKey — the same key space
// RowIdentifier.FormatKey uses and the InputDeltaGrid renders via
// getFormattedValue. Behavior matches getFormattedValue so axis/tooltip read
// identically to the grid beside them: $ prefixes currency, Percent scales
// ×100 and appends %, General/currency divide by the magnitude factor (no
// K/M suffix); all get the FormatKey's decimal places and thousands
// separators. An unresolved key (null) or a Date key falls back to a plain
// thousands-separated integer — a chart axis has no use for a formatted date,
// and the x-axis already carries the periods.
const makeValueFormatter = (formatKey: number | null) => {
  const params =
    formatKey == null
      ? undefined
      : (formatParameters.find((p) => p.formatKey === formatKey) as
          | IFormatOutputParameters
          | undefined);

  return (value: number): string => {
    if (!params || !('magnitudeTruncation' in params)) {
      return Highcharts.numberFormat(value, 0, '.', ',');
    }
    const { numberType, magnitudeTruncation, decimalPlaces } = params;
    if (numberType === 'Percent') {
      return `${Highcharts.numberFormat(value * 100, decimalPlaces, '.', ',')}%`;
    }
    const divisor = TRUNCATION_DIVISOR[magnitudeTruncation] ?? 1;
    const prefix = numberType === '$' ? '$' : '';
    return `${prefix}${Highcharts.numberFormat(
      value / divisor,
      decimalPlaces,
      '.',
      ','
    )}`;
  };
};

const InputDeltaChart: React.FC<InputDeltaChartProps> = ({
  input,
  compact = false,
  yearWindow,
}) => {
  const chartHeight = compact ? 260 : 380;

  // Exec AI year window (C1): clip the x-axis (input.x) and each series
  // (before/after, positionally aligned to x) to the window. yearKeepMask is
  // a no-op when the window is inactive or a label carries no parseable year,
  // so this is safe for period-labelled ("Month N") axes too.
  const { series, categories } = useMemo(() => {
    const rawX = input.x ?? [];
    const rawSeries = input.series ?? [];
    const mask = yearKeepMask(rawX, yearWindow);
    if (mask.every(Boolean)) {
      return { series: rawSeries, categories: rawX };
    }
    return {
      categories: applyKeepMask(rawX, mask),
      series: rawSeries.map((row) => ({
        ...row,
        before: applyKeepMask(row.before, mask),
        after: applyKeepMask(row.after, mask),
      })),
    };
  }, [input.x, input.series, yearWindow]);

  // A time-series input's columns share one FormatKey, so take the first
  // resolved one. Fall back to the legacy `format` hint (percent -> key 28,
  // Percent at 1 decimal, matching this chart's prior percent behavior)
  // when columns_meta is absent (pre-columns_meta proposals), then to null
  // (plain numbers).
  const formatKey = useMemo<number | null>(() => {
    const fromMeta = input.columns_meta?.find(
      (c) => c.format_key != null
    )?.format_key;
    if (fromMeta != null) return fromMeta;
    return input.format === 'percent' ? 28 : null;
  }, [input.columns_meta, input.format]);

  const formatValue = useMemo(() => makeValueFormatter(formatKey), [formatKey]);

  const options = useMemo<Highcharts.Options>(() => {
    const highchartsSeries: Highcharts.SeriesLineOptions[] = [];
    series.forEach((row, idx) => {
      const color = colors[idx % colors.length];
      highchartsSeries.push({
        type: 'line',
        name: `${row.label} — current`,
        data: row.before,
        color,
        dashStyle: 'Solid',
        lineWidth: 2,
      });
      highchartsSeries.push({
        type: 'line',
        name: `${row.label} — proposed`,
        data: row.after,
        color,
        dashStyle: 'Dash',
        lineWidth: 2,
      });
    });

    return {
      chart: {
        type: 'line',
        height: chartHeight,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      xAxis: {
        // Categories, never dates — x carries verbatim ValueHeaders
        // labels (calendar years or "Month N" period axes both pass
        // through unparsed).
        categories,
        labels: { style: { fontSize: '0.8125rem' } },
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            return formatValue(this.value as number);
          },
          style: { fontSize: '0.8125rem' },
        },
      },
      legend: {
        enabled: true,
        itemStyle: { fontSize: '0.8125rem', fontWeight: 'normal' },
      },
      tooltip: {
        // FormatKey-aware and always on: every value formats through the
        // same key space the InputDeltaGrid uses, so a currency input shows $,
        // magnitude-truncated values are divided down, percents append %.
        shared: true,
        pointFormatter(this: Highcharts.Point) {
          return `<span style="color:${this.color}">●</span> ${
            this.series.name
          }: <b>${formatValue(this.y ?? 0)}</b><br/>`;
        },
      },
      plotOptions: {
        series: {
          marker: { enabled: categories.length === 1 },
        },
      },
      series: highchartsSeries,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- series/categories/formatValue/chartHeight cover the payload's variable fields
  }, [series, categories, formatValue, chartHeight]);

  // mb only, no top margin — spacing parity with InputDeltaGrid
  // (2026-08-23, screenshot -20).
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {input.input_name}
      </Typography>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};

export default InputDeltaChart;
